import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FFPaper from '../../shared/FFPaper';
import { Fragment, useContext } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import Title from '../../shared/Title';
import { FunctionComponent } from 'react';
import KebabMenu, { defaultMenus } from '../../shared/KebabMenu';
import ChartContainer from '../../shared/ChartContainer';
import { TrafficAnalyticsContext } from '../../../context/trafficanalytics-context';
import BlueSkeleton from '../../shared/BlueSkeleton';

const handler = (menu: string) => {
    console.log(menu);
}

const COLORS = ['#21539B', '#A0D5FF'];

const RADIAN = Math.PI / 180;


type CircleLegendProps = {
    label: string,
    color: string,
    value: string
}

const CircleLegend: FunctionComponent<CircleLegendProps> = ({ label, color, value }) => {
    return (
        <Stack direction="column" sx={{ alignItems: 'left' }} spacing={1}>
            <Stack direction="row" sx={{
                padding: '10px 10px 10px 10px',
            }}>
                <Box sx={{
                    width: 20,
                    borderRadius: '50%',
                    height: 20,
                    backgroundColor: color,
                }}>&nbsp;
                </Box>
                <Typography sx={{
                    padding: '0px 15px 10px 20px',
                }}>
                    {label}
                </Typography>
                <Typography sx={{
                    padding: '0px 0px 10px 25px',
                }}>
                    {value}
                </Typography>
            </Stack>
        </Stack>
    )
}


const renderCustomizedLabel = (data: { cx: number, cy: number, midAngle: number, innerRadius: number, outerRadius: number, percent: number, index: number }) => {

    const radius = data.innerRadius + (data.outerRadius - data.innerRadius) * 0.5;
    const x = data.cx + radius * Math.cos(-data.midAngle * RADIAN);
    const y = data.cy + radius * Math.sin(-data.midAngle * RADIAN);

    return (
        <text x={x} y={y} fill="black" textAnchor='middle' dominantBaseline="central" fontSize={11} >
            {`${(data.percent * 100).toFixed(0)}%`}
        </text>
    );
};


interface ChartProps {
    data?: Record<string, number>;
}

const processData = (data: Record<string, number>) => {

    const newData: Record<string, any>[] = [];

    Object.entries(data).forEach(

        ([key, val]) => {
            if (key.indexOf('Count') > 0) {
                newData.push({
                    name: key,
                    value: val
                });
            }
        }
    );

    return newData;
};

function WeekTrafficPieChart({ data }: ChartProps) {

    const chartData = data ? processData(data) : [];

    const renderLegend = (props: any) => {
        const { payload } = props;

        return (
            <Box sx={{
                pl: {
                    xs: '0',
                    sm: '50%',
                    md: '50%'
                },
                pt: {
                    xs: '0%',
                    sm: '0%',
                    md: '0%'
                },
                mt: {
                    xs: '0%',
                    sm: -17,
                    md: -17
                }
            }}>
                {
                    payload.map((entry: any, i: number) => {
                        const { payload: item } = entry;
                        const label = `${item.name}`
                        const value = ` ${item.value}`
                        return (
                            <Box key={i} >
                                <CircleLegend label={label} value={value} color={entry.color} />
                            </Box>
                        )
                    })
                }
            </Box>
        )
    }

    return (
        <Fragment>
            <ResponsiveContainer >
                <PieChart  >
                    <Pie
                        data={chartData}
                        labelLine={false}
                        innerRadius={25}
                        outerRadius={70}
                        cy={80}
                        cx={70}
                        dataKey="value"
                        label={renderCustomizedLabel}

                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Legend content={renderLegend} />
                </PieChart>
            </ResponsiveContainer>
        </Fragment>
    );
}


export default function WeekTrafficPieCard() {
    const info = 'Measures the amount of weekend traffic vs week traffic';
    const { status, data, isFetching } = useContext(TrafficAnalyticsContext);


    return (

        <Grid item={true} xs={12} md={12} lg={5} >
            <FFPaper
                elevation={3}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    pb: 3,
                    position: 'relative',
                    justifyContent: 'top',
                }}
            >
                <KebabMenu
                    menus={defaultMenus}
                    handler={handler}
                />
                <Box sx={{
                    p: '8px'
                }}>
                    <Title big={true} info={info}>Week/Weekend Traffic</Title>
                </Box>
                <Box sx={{
                    height: '100%',
                    display: 'flex', justifyContent: 'center', width: '100%'
                }}>
                    <Box sx={{
                        width: '80%', 
                        maxWidth: 600,
                        minHeight: {
                            xs: 300,
                            sm: 180,
                            md: 180
                        }
                    }}>

                        {isFetching && <BlueSkeleton width={'100%'} height={'100%'} sx={{ mt: 1, ml: 'auto', mr: 'auto' }} />}

                        {status === 'success' && !isFetching && <ChartContainer>
                            <WeekTrafficPieChart data={data?.trafficAnalyticsWeekVsWeekend.value} />
                        </ChartContainer>}

                    </Box>
                </Box>
            </FFPaper>
        </Grid>

    )

}
